import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Building2, Plus, Pencil, Trash2, Loader2, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function BranchesPage() {
  const { authHeaders, API, refreshBranches, branches: authBranches } = useAuth();
  const { t } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', subdomain: '', address: '', phone: '', receipt_paper_width: 80 });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchBranches(); }, []);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/branches`, authHeaders());
      setBranches(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error(t('branches.nameRequired')); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/branches/${editingId}`, form, authHeaders());
        toast.success(t('branches.updated'));
      } else {
        await axios.post(`${API}/branches`, form, authHeaders());
        toast.success(t('branches.created'));
      }
      setDialogOpen(false);
      setForm({ name: '', subdomain: '', address: '', phone: '', receipt_paper_width: 80 });
      setEditingId(null);
      fetchBranches();
      refreshBranches();
    } catch (err) { toast.error(t('branches.saveFailed')); }
    finally { setSaving(false); }
  };

  const handleEdit = (branch) => {
    setForm({ name: branch.name, subdomain: branch.subdomain || '', address: branch.address || '', phone: branch.phone || '', receipt_paper_width: branch.receipt_paper_width || 80 });
    setEditingId(branch.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('branches.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/branches/${id}`, authHeaders());
      toast.success(t('branches.deleted'));
      fetchBranches();
      refreshBranches();
    } catch (err) { toast.error(t('branches.deleteFailed')); }
  };

  const toggleActive = async (branch) => {
    try {
      await axios.put(`${API}/branches/${branch.id}`, { is_active: !branch.is_active }, authHeaders());
      toast.success(branch.is_active ? t('branches.deactivated') : t('branches.activated'));
      fetchBranches();
    } catch (err) { toast.error(t('branches.updateFailed')); }
  };

  return (
    <ModuleLayout moduleKey="settings">
      <div className="space-y-6" data-testid="branches-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-cairo font-bold text-2xl text-foreground">{t('branches.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('branches.subtitle')}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setForm({ name: '', subdomain: '', address: '', phone: '', receipt_paper_width: 80 }); setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-branch-btn">
                <Plus className="w-4 h-4 mr-2" /> {t('branches.addBranch')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-cairo">{editingId ? t('branches.editBranch') : t('branches.newBranch')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSave} className="space-y-4 mt-4">
                <div>
                  <Label className="text-sm mb-1.5 block">{t('branches.branchName')}</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="branch-name-input" placeholder="Main Branch - Riyadh" />
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">{t('branches.subdomain')}</Label>
                  <Input value={form.subdomain} onChange={e => setForm({ ...form, subdomain: e.target.value })} data-testid="branch-subdomain-input" placeholder="riyadh" />
                  <p className="text-xs text-muted-foreground mt-1">riyadh.zouqai.com</p>
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">{t('branches.address')}</Label>
                  <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="branch-address-input" placeholder="Olaya Street, Riyadh" />
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">{t('branches.phone')}</Label>
                  <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="branch-phone-input" placeholder="+966501234567" />
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">{t('branches.paperWidth')}</Label>
                  <div className="inline-flex rounded-lg border border-border overflow-hidden" data-testid="branch-paper-width-toggle">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, receipt_paper_width: 80 })}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${form.receipt_paper_width === 80 ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                      data-testid="branch-paper-80mm"
                    >80mm</button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, receipt_paper_width: 58 })}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${form.receipt_paper_width === 58 ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                      data-testid="branch-paper-58mm"
                    >58mm</button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">{t('branches.paperWidthHint')}</p>
                </div>
                <Button type="submit" disabled={saving} className="w-full rounded-full" data-testid="branch-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? t('common.update') : t('common.create'))}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : branches.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{t('branches.noBranches')}</p>
            <p className="text-xs mt-1">{t('branches.addFirst')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map(branch => (
              <Card key={branch.id} className="bg-card border border-border/50 rounded-xl overflow-hidden transition-all duration-200 hover:shadow-md hover:border-border" data-testid={`branch-card-${branch.id}`}>
                {/* Color strip top */}
                <div className={`h-1 ${branch.is_active ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gray-300 dark:bg-gray-700'}`} />
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${branch.is_active ? 'bg-primary/10' : 'bg-muted'}`}>
                        <Building2 className={`w-5 h-5 ${branch.is_active ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{branch.name}</h3>
                        <p className="text-xs text-primary">{branch.subdomain}.zouqai.com</p>
                      </div>
                    </div>
                    <Badge variant={branch.is_active ? 'default' : 'secondary'} className={`text-[10px] ${branch.is_active ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/25' : ''}`}>
                      {branch.is_active ? t('branches.active') : t('branches.inactive')}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    {branch.address && (
                      <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground/60" />
                        <span className="truncate">{branch.address}</span>
                      </div>
                    )}
                    {branch.phone && (
                      <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                        <Phone className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground/60" />
                        <span>{branch.phone}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-border/50">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={branch.is_active}
                        onCheckedChange={() => toggleActive(branch)}
                        data-testid={`branch-toggle-${branch.id}`}
                      />
                      <span className="text-xs text-muted-foreground">{branch.is_active ? t('branches.active') : t('branches.inactive')}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(branch)} data-testid={`edit-branch-${branch.id}`} className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(branch.id)} data-testid={`delete-branch-${branch.id}`} className="h-8 w-8 p-0 rounded-lg text-destructive hover:bg-destructive/10">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </ModuleLayout>
  );
}
